import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { formatPence, parseSimpleText } from '@hcd/domain'
import { ChevronLeft, Printer } from 'lucide-react'
import { Logo } from '@/components/brand/logo'
import { formatDate, formatDateTime } from '@/components/proposal/format'
import { SignatureMark } from '@/components/proposal/presentation'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { contractQuery, type ContractDetail } from '@/lib/contracts'
import { PER } from '@/lib/quotes'
import { meQuery } from '@/lib/session'
import { useTitle } from '@/lib/use-title'

/**
 * The signed agreement as a clean A4 document: save it as a PDF from the
 * browser's print dialog. Staff only; the client has their own copy on their link.
 */
export const Route = createFileRoute('/contracts/$id/print')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } })
    if (me.mustChangePassword) throw redirect({ to: '/change-password' })
  },
  component: PrintPage,
})

const PRINT_CSS = `
@page { size: A4; margin: 16mm 15mm 18mm; }
@media print {
  html, body { background: #fff !important; color: #111 !important; }
  .doc { box-shadow: none !important; border: 0 !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
  .doc * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .avoid-break { break-inside: avoid; }
  .page-break { break-before: page; }
  a { color: inherit !important; text-decoration: none !important; }
}
`

function PrintPage() {
  const { id } = Route.useParams()
  const { data: c, isPending, isError, error } = useQuery(contractQuery(id))
  useTitle(c ? `${c.reference} signed agreement, ${c.customerName}` : 'Signed agreement')
  return (
    <div className="min-h-dvh bg-muted/60 print:bg-white">
      <style>{PRINT_CSS}</style>
      <div className="sticky top-0 z-50 flex flex-wrap items-center gap-3 border-b bg-card/95 px-4 py-2 backdrop-blur print:hidden">
        <Link
          to="/contracts/$id"
          params={{ id }}
          className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-medium hover:bg-secondary"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Back to contract
        </Link>
        <span className="flex-1 text-sm text-muted-foreground max-sm:hidden">
          To keep a PDF, choose Save as PDF in the print window.
        </span>
        <Button className="h-9" onClick={() => window.print()} disabled={!c}>
          <Printer />
          Print or save as PDF
        </Button>
      </div>
      {isError ? (
        <p role="alert" className="p-6 text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <Skeleton className="mx-auto my-6 h-[80dvh] max-w-[210mm] rounded-xl" />
      ) : (
        <SignedAgreement c={c} />
      )}
    </div>
  )
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="mt-8 mb-3 border-b border-[#0b2a5b]/20 pb-1.5 text-[13px] font-semibold tracking-[0.08em] text-[#0b2a5b] uppercase">
      {children}
    </h2>
  )
}

function SignedAgreement({ c }: { c: ContractDetail }) {
  const company = c.company
  const blocks = parseSimpleText(c.agreement.text)
  const hcd = c.signatures.hcd
  const client = c.signatures.client
  const ev = c.evidence
  const utc = (iso: string) =>
    new Date(iso)
      .toISOString()
      .replace('T', ' ')
      .replace(/\.\d+Z$/, ' UTC')
  return (
    <main className="doc mx-auto my-6 max-w-[210mm] rounded-lg border bg-white px-[15mm] py-[16mm] text-[10.5pt] leading-relaxed text-[#111] shadow-sm max-sm:mx-2 max-sm:px-5 max-sm:py-8">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-[#0b2a5b] pb-5">
        <Logo className="h-10" />
        {company && (
          <address className="text-right text-[9pt] leading-snug text-[#333] not-italic max-sm:text-left">
            <strong className="block text-[10pt] text-[#111]">{company.legalName}</strong>
            {company.registeredAddress}
            <br />
            Company number {company.companyNumber}
            {company.vatNumber && ` · VAT ${company.vatNumber}`}
            {company.phone && (
              <>
                <br />
                {company.phone}
                {company.email && ` · ${company.email}`}
              </>
            )}
          </address>
        )}
      </header>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[9pt] font-semibold tracking-[0.1em] text-[#0b2a5b] uppercase">
            {c.contractTypeName || 'Service agreement'}
          </p>
          <h1 className="text-[20pt] leading-tight font-semibold">{c.customerName}</h1>
          {c.title && c.title !== c.customerName && <p className="text-[#444]">{c.title}</p>}
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 text-[9pt]">
          <dt className="text-[#555]">Contract</dt>
          <dd className="font-semibold">{c.reference}</dd>
          <dt className="text-[#555]">Proposal</dt>
          <dd>
            {c.quoteReference}
            {c.revision && c.revision > 1 ? ` rev ${c.revision}` : ''}
          </dd>
          <dt className="text-[#555]">Signed</dt>
          <dd>{formatDate(c.signedAt)}</dd>
          {c.startDate && (
            <>
              <dt className="text-[#555]">Start</dt>
              <dd>{formatDate(c.startDate)}</dd>
            </>
          )}
        </dl>
      </div>

      <SectionTitle>Parties</SectionTitle>
      <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
        <div>
          <p className="text-[9pt] text-[#555]">Supplier</p>
          <p className="font-semibold">{company?.legalName ?? 'HCD Cleaning'}</p>
          <p className="text-[9.5pt] text-[#333]">{company?.registeredAddress}</p>
        </div>
        <div>
          <p className="text-[9pt] text-[#555]">Customer</p>
          <p className="font-semibold">{c.customerName}</p>
          <p className="text-[9.5pt] text-[#333]">
            {c.customer?.registeredAddress || c.client?.siteAddress}
            {c.customer?.companyNumber && (
              <>
                <br />
                Company number {c.customer.companyNumber}
              </>
            )}
          </p>
        </div>
      </div>

      {blocks.length > 0 && (
        <>
          <SectionTitle>Agreement</SectionTitle>
          <div className="grid gap-2.5">
            {blocks.map((b, i) =>
              b.type === 'heading' ? (
                <h3 key={i} className="mt-2 font-semibold">
                  {b.text}
                </h3>
              ) : (
                <p key={i}>{b.text}</p>
              ),
            )}
          </div>
        </>
      )}

      {c.pricing && (
        <div className="avoid-break">
          <SectionTitle>Services and charges</SectionTitle>
          <table className="w-full border-collapse text-[9.5pt]">
            <thead>
              <tr className="border-b border-[#999] text-left text-[8.5pt] text-[#555]">
                <th className="py-1.5 pr-3 font-medium">Service</th>
                <th className="py-1.5 pr-3 font-medium">Detail</th>
                <th className="py-1.5 text-right font-medium">Charge (ex VAT)</th>
              </tr>
            </thead>
            <tbody>
              {c.pricing.lines.map((l) => (
                <tr key={l.id} className="border-b border-[#ddd] align-top">
                  <td className="py-1.5 pr-3">{l.description}</td>
                  <td className="py-1.5 pr-3 text-[#444]">
                    {l.basis === 'days'
                      ? `${l.daysLabel} × ${formatPence(l.unitPricePence)}`
                      : `${l.quantity} × ${formatPence(l.unitPricePence)} per ${l.unitName}`}
                    {l.chargeType === 'recurring' && l.frequency
                      ? `, ${PER[l.frequency]}`
                      : ', one-off'}
                  </td>
                  <td className="tabular py-1.5 text-right">{formatPence(l.netPence)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="mt-3 ml-auto text-[9.5pt]">
            <tbody>
              {c.pricing.periods.map((p) => (
                <tr key={p.key}>
                  <td className="pr-6 text-[#444]">Total {PER[p.key] ?? p.key}</td>
                  <td className="tabular text-right">
                    {formatPence(p.netPence)} + VAT {formatPence(p.vatPence)} ={' '}
                    <strong>{formatPence(p.grossPence)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {c.agreement.paymentTerms && (
            <p className="mt-3 text-[9.5pt] text-[#333]">{c.agreement.paymentTerms}</p>
          )}
        </div>
      )}

      <div className="avoid-break">
        <SectionTitle>Signatures</SectionTitle>
        {c.agreement.declarationText && (
          <p className="mb-4 text-[9.5pt] text-[#333]">
            {c.agreement.declarationText} {c.agreement.acceptanceNote}
          </p>
        )}
        <div className="grid gap-8 sm:grid-cols-2 print:grid-cols-2">
          <div>
            <p className="mb-1 text-[9pt] text-[#555]">
              For {company?.legalName ?? 'HCD Cleaning'}
            </p>
            {hcd ? (
              <>
                <SignatureMark type={hcd.type} text={hcd.text} image={hcd.image} name={hcd.name} />
                <p className="mt-2 text-[9.5pt]">
                  <strong>{hcd.name}</strong>, {hcd.role}
                  <br />
                  {formatDateTime(hcd.signedAt)}
                </p>
              </>
            ) : (
              <p className="text-[#555]">Not recorded</p>
            )}
          </div>
          <div>
            <p className="mb-1 text-[9pt] text-[#555]">For {c.customerName}</p>
            {client ? (
              <>
                <SignatureMark
                  type={client.type}
                  text={client.text}
                  image={client.image}
                  name={client.name}
                />
                <p className="mt-2 text-[9.5pt]">
                  <strong>{client.name}</strong>, {client.position}
                  <br />
                  {formatDateTime(client.signedAt)}
                </p>
              </>
            ) : (
              <p className="text-[#555]">Not recorded</p>
            )}
          </div>
        </div>
      </div>

      {ev && (
        <section className="page-break avoid-break mt-10 rounded-md border border-[#0b2a5b]/30 bg-[#f4f7fb] p-5 text-[9pt] print:mt-0">
          <h2 className="mb-1 text-[11pt] font-semibold text-[#0b2a5b]">Evidence certificate</h2>
          <p className="mb-3 text-[#444]">
            Electronic acceptance recorded by HCD Portal. The fingerprint below is the SHA-256 hash
            of the exact proposal the client saw and signed; any change to that proposal would
            change it.
          </p>
          <dl className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-4 gap-y-1.5">
            <dt className="text-[#555]">Contract</dt>
            <dd>{c.reference}</dd>
            <dt className="text-[#555]">Proposal</dt>
            <dd>
              {ev.reference || c.quoteReference}
              {ev.revision ? `, revision ${ev.revision}` : ''}
            </dd>
            <dt className="text-[#555]">Signed by</dt>
            <dd>
              {client ? `${client.name}, ${client.position} (${client.email})` : c.clientSignerName}
            </dd>
            <dt className="text-[#555]">Accepted (UTC)</dt>
            <dd className="tabular">{utc(ev.acceptedAtUtc)}</dd>
            <dt className="text-[#555]">Accepted (UK time)</dt>
            <dd className="tabular">{formatDateTime(ev.acceptedAtUtc)}</dd>
            {hcd && (
              <>
                <dt className="text-[#555]">HCD signed (UTC)</dt>
                <dd className="tabular">{utc(hcd.signedAt)}</dd>
              </>
            )}
            <dt className="text-[#555]">IP address</dt>
            <dd className="tabular">{ev.ip || 'Not recorded'}</dd>
            <dt className="text-[#555]">Device</dt>
            <dd className="break-words">{ev.userAgent || 'Not recorded'}</dd>
            <dt className="text-[#555]">Fingerprint (SHA-256)</dt>
            <dd className="font-mono text-[8.5pt] break-all">{ev.contentHash}</dd>
          </dl>
        </section>
      )}
    </main>
  )
}
